/**
 * Verksamhetsansvar (Raindance) — sökbar combobox + IKPART-label + historik.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

export interface BusinessResponsibilityOption {
  id: number;
  ansvarId: string;
  ansvarText: string;
  ikpartId: string | null;
  ikpartText: string | null;
  label: string;
  ikpartLabel: string | null;
}

export interface BusinessResponsibilityHistoryEntry {
  id: number;
  ansvarId: string | null;
  ansvarText: string | null;
  previousAnsvarId: string | null;
  previousAnsvarText: string | null;
  assignedAt: string;
  assignedByName: string | null;
}

interface BusinessResponsibilityFieldProps {
  vehicleId: number;
  organizationId: string;
  value: string;
  onChange: (value: string) => void;
  /** Nuvarande val om det saknas i sökträffar */
  currentLabel?: string | null;
  /** Nuvarande IKPART från sparad rad */
  savedIkpartLabel?: string | null;
}

function formatLocalDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export const BusinessResponsibilityField: React.FC<BusinessResponsibilityFieldProps> = ({
  vehicleId,
  organizationId,
  value,
  onChange,
  currentLabel,
  savedIkpartLabel,
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<BusinessResponsibilityOption[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [history, setHistory] = useState<BusinessResponsibilityHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const prevOrganizationIdRef = useRef(organizationId);

  useEffect(() => {
    if (prevOrganizationIdRef.current !== organizationId) {
      prevOrganizationIdRef.current = organizationId;
      if (value) onChange('');
      setSearch('');
    }
  }, [organizationId, value, onChange]);

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedSearch(search.trim()), 280);
    return () => clearTimeout(tmr);
  }, [search]);

  const loadOptions = useCallback(async () => {
    if (!organizationId) {
      setOptions([]);
      return;
    }
    setLoadingOptions(true);
    try {
      const res = await api.get<{ success: boolean; data: BusinessResponsibilityOption[] }>(
        '/api/vehicles/business-responsibilities',
        {
          params: {
            organizationId,
            search: debouncedSearch || undefined,
            limit: 80,
          },
        }
      );
      setOptions(res.data.success ? res.data.data || [] : []);
    } catch {
      setOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  }, [organizationId, debouncedSearch]);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get<{ success: boolean; data: BusinessResponsibilityHistoryEntry[] }>(
        `/api/vehicles/${vehicleId}/business-responsibility-history`
      );
      setHistory(res.data.success ? res.data.data || [] : []);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory, value]);

  const selectItems = useMemo(() => {
    const mapped = options.map((o) => ({
      id: String(o.id),
      label: o.label,
    }));
    if (value && currentLabel && !mapped.some((m) => m.id === value)) {
      return [{ id: value, label: currentLabel }, ...mapped];
    }
    return mapped;
  }, [options, value, currentLabel]);

  const selectedOption = options.find((o) => String(o.id) === value);
  const ikpartLabel = selectedOption?.ikpartLabel ?? savedIkpartLabel ?? null;

  const historyLabel = (row: BusinessResponsibilityHistoryEntry) => {
    if (row.ansvarId && row.ansvarText) return `${row.ansvarId} - ${row.ansvarText}`;
    return t('vehicleCard.general.businessResponsibilityCleared');
  };

  const previousLabel = (row: BusinessResponsibilityHistoryEntry) => {
    if (row.previousAnsvarId && row.previousAnsvarText) {
      return `${row.previousAnsvarId} - ${row.previousAnsvarText}`;
    }
    return '—';
  };

  return (
    <div className="space-y-2">
      <div>
        <label className="text-xs text-textSecondary block mb-1">
          {t('vehicleCard.general.businessResponsibility')}
        </label>
        <SearchableSelect
          items={selectItems}
          value={value}
          onChange={onChange}
          placeholder={t('vehicleCard.general.businessResponsibilitySearch')}
          emptyOptionLabel={t('vehicleCard.general.noBusinessResponsibility')}
          onSearchChange={setSearch}
          externalSearch={search}
          isLoading={loadingOptions}
        />
      </div>
      {ikpartLabel && (
        <p className="text-xs text-textSecondary">
          <span className="font-medium text-textDefault">
            {t('vehicleCard.general.businessResponsibilityIkpart')}:
          </span>{' '}
          {ikpartLabel}
        </p>
      )}
      <div>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => setShowHistory((v) => !v)}
        >
          {showHistory
            ? t('vehicleCard.general.businessResponsibilityHideHistory')
            : t('vehicleCard.general.businessResponsibilityShowHistory')}
        </button>
        {showHistory && (
          <div className="mt-2 rounded-lg border border-border bg-bgBase overflow-hidden">
            {loadingHistory && (
              <p className="px-3 py-2 text-xs text-textSecondary">{t('common.loading')}</p>
            )}
            {!loadingHistory && history.length === 0 && (
              <p className="px-3 py-2 text-xs text-textSecondary">
                {t('vehicleCard.general.businessResponsibilityNoHistory')}
              </p>
            )}
            {!loadingHistory && history.length > 0 && (
              <ul className="divide-y divide-border max-h-48 overflow-auto">
                {history.map((row) => (
                  <li key={row.id} className="px-3 py-2 text-xs">
                    <div className="font-medium text-textDefault">{historyLabel(row)}</div>
                    <div className="text-textSecondary mt-0.5">
                      {t('vehicleCard.general.businessResponsibilityFrom')}: {previousLabel(row)}
                    </div>
                    <div className="text-textTertiary mt-0.5">
                      {formatLocalDateTime(row.assignedAt)}
                      {row.assignedByName ? ` · ${row.assignedByName}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessResponsibilityField;
